/**
 * Spotting a second tool that reports the same sale to the same pixel.
 *
 * Measured on 6 August 2026 on a WooCommerce shop running Pixel Manager next to
 * us. Meta listed order 29 twice: `ord_29` from our server and `pmw_29` from
 * Pixel Manager, both counted. One sale, two conversions, and neither tool said
 * a word about it.
 *
 * `fbq` is a single global. We initialise our pixel and scope every call we make
 * with `trackSingle`, so our events reach our dataset and nobody else's. A tool
 * that calls `fbq("track", ...)` without scoping reaches EVERY initialised
 * pixel, ours included, carrying its own event id. Meta deduplicates on the
 * event id, sees two different ones, and keeps both.
 *
 * So the signal is not "another pixel exists". Two pixels on one page are
 * normal and harmless. The signal is "a conversion arrived at our pixel that we
 * did not send", and that is exactly what this decides.
 */

/** A conversion reached our pixel from something that is not us. */
export const CONFLICT_FOREIGN_CONVERSION = "foreign_conversion";

/** An fbq already existed when we loaded. Weaker: it says another tool is on the
 *  page, not that it double counts. Kept apart for that reason. */
export const CONFLICT_PREEXISTING_PIXEL = "preexisting_pixel";

export type PixelConflict = typeof CONFLICT_FOREIGN_CONVERSION | typeof CONFLICT_PREEXISTING_PIXEL;

/**
 * A page view is not a conversion.
 *
 * Duplicate page views cost nothing, they are already filtered elsewhere, and
 * warning about them would bury the one message that matters.
 */
const NOT_A_CONVERSION = "PageView";

interface CallOptions {
  eventID?: unknown;
}

/**
 * Does this fbq call put a conversion on our pixel that we did not send?
 *
 * @param args      the raw fbq arguments, exactly as the SDK received them
 * @param ourPrefix the prefix every event id we generate starts with
 * @param ourPixelId the pixel we initialised
 */
export function isForeignConversion(
  args: readonly unknown[],
  ourPrefix: string,
  ourPixelId: string,
): boolean {
  const method = args[0];

  if (method === "track" || method === "trackCustom") {
    // Unscoped: fans out to every initialised pixel, so it lands on ours.
    const eventName = args[1];
    if (eventName === NOT_A_CONVERSION) return false;
    return !isOurs(args[3], ourPrefix);
  }

  if (method === "trackSingle" || method === "trackSingleCustom") {
    // Scoped. Only a problem when it is aimed at our pixel.
    if (String(args[1]) !== String(ourPixelId)) return false;
    const eventName = args[2];
    if (eventName === NOT_A_CONVERSION) return false;
    return !isOurs(args[4], ourPrefix);
  }

  return false;
}

function isOurs(options: unknown, ourPrefix: string): boolean {
  if (typeof options !== "object" || options === null) return false;
  const eventId = (options as CallOptions).eventID;
  if (typeof eventId !== "string") return false;
  return eventId.indexOf(ourPrefix) === 0;
}
